import React, { useCallback } from 'react';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import { noop } from 'lodash';

import ReactTextareaAutosize from 'react-textarea-autosize';

import { Button, Input, Label, FormGroup, FormFeedback } from 'reactstrap';
import { Icon } from 'js/components';
import { faConveyorBeltBoxes } from '@fortawesome/pro-solid-svg-icons';

import styles from './ManagedProductForm.module.scss';

const AddMemberForm = ({
  managedProduct,
  values,
  errors,
  handleChange,
  handleBlur,
  handleReset,
  touched,
  isSubmitting,
  handleSubmit,
  mode = 'create',
  onCancel = noop,
  onArchive = noop,
  onUnarchive = noop,
}) => {
  const formikAttributes = (fieldName) => ({
    id: fieldName,
    name: fieldName,
    value: values[fieldName],
    onChange: handleChange,
    onBlur: handleBlur,
    invalid: errors[fieldName] && touched[fieldName],
  });

  const formikFeedback = (fieldName) => {
    return errors[fieldName] && touched[fieldName] ? (
      <FormFeedback>{errors[fieldName]}</FormFeedback>
    ) : null;
  };

  const handleCancel = useCallback(() => {
    handleReset();
    onCancel();
  }, [handleReset, onCancel]);

  return (
    <form
      onSubmit={handleSubmit}
      className={`form ${styles['managed-product-card']}`}
    >
      <div className={styles['managed-product-avatar']}>
        <div className={styles['managed-product-avatar-image']}>
          <Icon icon={faConveyorBeltBoxes} size="lg" />
        </div>
      </div>
      <div className={styles['managed-product-content']}>
        <div className={styles['managed-product-name']}>
          <Label for="name">Name</Label>
          <FormGroup>
            <Input type="text" {...formikAttributes('name')} autoFocus />
            {formikFeedback('name')}
          </FormGroup>
        </div>
        <div className={styles['managed-product-description']}>
          <Label for="description">Description</Label>
          <FormGroup>
            <ReactTextareaAutosize
              {...formikAttributes('description')}
              className="form-control autosize"
              style={{ width: '100%' }}
              minRows={3}
              maxRows={20}
            />
            {formikFeedback('description')}
          </FormGroup>
        </div>
      </div>
      <div className={styles['managed-product-actions']}>
        {mode === 'edit' ? (
          <>
            <Button color="primary" disabled={isSubmitting} type="submit">
              Update
            </Button>
            <Button
              color="secondary"
              disabled={isSubmitting}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            {managedProduct.archived_state === 'archived' ? (
              <Button
                color="secondary"
                disabled={isSubmitting}
                onClick={onUnarchive}
              >
                Restore
              </Button>
            ) : (
              <Button
                color="danger"
                disabled={isSubmitting}
                onClick={onArchive}
              >
                Archive
              </Button>
            )}
          </>
        ) : (
          <>
            <Button color="primary" disabled={isSubmitting} type="submit">
              <Icon icon="plus" /> Add product
            </Button>
            <Button
              color="secondary"
              disabled={isSubmitting}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </>
        )}
      </div>
    </form>
  );
};

const FormikAddMemberForm = withFormik({
  mapPropsToValues: ({
    managedProduct: { name = null, description = null } = {},
  }) => {
    return {
      name: name ?? '',
      description: description ?? '',
    };
  },
  validationSchema: Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(
        "A product without a name is like a book without a title - let's give it one"
      )
      .max(
        128,
        "That's a long name you got there. Unfortunately we cut you off at 128 characters."
      ),
    description: Yup.string().trim(),
  }),
  handleSubmit: async (values, bag) => {
    const {
      setSubmitting,
      props: { onSubmit },
    } = bag;
    // do nothing
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  },
  displayName: 'AddManagedProduct',
})(AddMemberForm);

export default FormikAddMemberForm;
